import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Loader2, TrendingUp, TrendingDown, AlertCircle, Calendar } from "lucide-react";

/**
 * AdminCrossChannelPNL — monthly grid of revenue/fees/net across every sales
 * channel (Nifty marketplaces + eBay + direct) plus operating expenses and
 * quarterly-tax reserves. Hand the CSV to her CPA in January and this IS
 * the year-end filing document.
 */
const CHANNEL_LABELS = {
  depop: "depop",
  poshmark: "poshmark",
  ebay: "eBay",
  mercari: "mercari",
  etsy: "etsy",
  direct: "direct site",
  other: "other",
};

const currentYear = new Date().getFullYear();

export default function AdminCrossChannelPNL() {
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = async (y) => {
    setData(null);
    try {
      const { data } = await api.get(`/accounting/pnl?year=${y}`);
      setData(data);
    } catch (e) {
      toast.error(`could not load p&l: ${e.response?.data?.detail || e.message}`);
      setData({ monthly: [], year_totals: null });
    }
  };
  useEffect(() => { load(year); }, [year]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/accounting/pnl.csv?year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `maison-pilet-pnl-${year}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("csv downloaded — hand it to your cpa.");
    } catch (e) {
      toast.error(`download failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 sm:p-10 space-y-6 max-w-7xl">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="editorial-label mb-1">analytics · finance</p>
            <h1 className="font-serif text-4xl text-charcoal">cross-channel P&amp;L</h1>
            <p className="text-espresso mt-2 max-w-3xl text-sm">
              nifty shows nifty. eBay shows eBay. this rolls every sales channel plus operating expenses into one
              monthly grid — your year-end tax filing document, live year-round.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="border border-taupe px-3 py-2 text-xs bg-ivory"
              data-testid="pnl-year-select"
            >
              {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              onClick={downloadCsv}
              disabled={downloading || !data?.monthly?.length}
              className="border border-brass bg-brass text-ivory hover:bg-charcoal disabled:opacity-40 px-4 py-2 text-[0.65rem] tracking-widest uppercase inline-flex items-center gap-2"
              data-testid="pnl-csv-download"
            >
              {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} export csv
            </button>
          </div>
        </header>

        {data === null && <p className="text-taupe text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> loading…</p>}

        {data && data.year_totals && (
          <>
            {/* Year totals strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard
                label="year net profit"
                value={`$${data.year_totals.net_profit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                tone={data.year_totals.net_profit >= 0 ? "positive" : "negative"}
              />
              <StatCard
                label="year gross profit"
                value={`$${data.year_totals.gross_profit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              />
              <StatCard
                label="operating expenses"
                value={`$${data.year_totals.operating_expenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              />
              <StatCard
                label="units sold"
                value={Object.values(data.year_totals.channels).reduce((n, c) => n + (c.units || 0), 0)}
              />
            </div>

            {/* Quarterly tax reserves */}
            <div className="border border-brass/60 bg-linen p-5" data-testid="pnl-quarterly-tax">
              <div className="flex items-start gap-3 mb-3">
                <AlertCircle className="w-4 h-4 text-brass mt-0.5" />
                <div>
                  <p className="editorial-label text-charcoal">quarterly tax reserves</p>
                  <p className="text-xs text-espresso mt-1">
                    set aside <strong>{Math.round((data.tax_reserve_rate || 0.3) * 100)}%</strong> of each quarter's net profit for federal SE tax + income tax + typical state.
                    deadlines: apr 15 (Q1), jun 15 (Q2), sep 15 (Q3), jan 15 (Q4).
                    <em className="block mt-1 text-taupe">estimate only — confirm with your CPA before you file the 1040-ES.</em>
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                {["Q1", "Q2", "Q3", "Q4"].map((q) => {
                  const qd = data.quarterly_tax[q];
                  return (
                    <div key={q} className="border border-taupe/40 bg-ivory p-3" data-testid={`pnl-quarter-${q}`}>
                      <p className="text-[0.6rem] tracking-widest uppercase text-taupe">{q} · net ${qd.net_profit.toFixed(2)}</p>
                      <p className="font-serif text-xl text-brass mt-1">${qd.reserve.toFixed(2)}</p>
                      <p className="text-[0.55rem] tracking-widest uppercase text-taupe mt-1">reserve</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Monthly grid */}
            <div className="border border-taupe/40 bg-ivory overflow-x-auto">
              <table className="w-full text-xs" data-testid="pnl-table">
                <thead className="bg-linen border-b border-taupe/40 sticky top-0">
                  <tr className="text-[0.55rem] tracking-widest uppercase text-taupe">
                    <th className="px-3 py-2.5 text-left">month</th>
                    {data.channels.map((c) => (
                      <th key={c} className="px-3 py-2.5 text-right">{CHANNEL_LABELS[c] || c}</th>
                    ))}
                    <th className="px-3 py-2.5 text-right">op exp</th>
                    <th className="px-3 py-2.5 text-right border-l border-taupe/30">net</th>
                  </tr>
                </thead>
                <tbody>
                  {data.monthly.map((m) => {
                    const rowNet = m.net_profit;
                    return (
                      <tr key={m.month} className="border-b border-taupe/20" data-testid={`pnl-row-${m.month}`}>
                        <td className="px-3 py-2 text-charcoal font-medium">
                          {new Date(m.month + "-01").toLocaleDateString([], { month: "short", year: "2-digit" })}
                        </td>
                        {data.channels.map((c) => {
                          const b = m.channels[c];
                          const hasActivity = b.units > 0 || b.revenue > 0;
                          return (
                            <td key={c} className={`px-3 py-2 text-right ${hasActivity ? "text-charcoal" : "text-taupe/40"}`}>
                              {hasActivity ? `$${b.net.toFixed(2)}` : "—"}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-right text-taupe">{m.operating_expenses ? `-$${m.operating_expenses.toFixed(2)}` : "—"}</td>
                        <td className={`px-3 py-2 text-right font-medium border-l border-taupe/30 ${rowNet >= 0 ? "text-emerald-800" : "text-red-800"}`}>
                          {rowNet >= 0 ? <TrendingUp className="inline w-3 h-3 mr-1" /> : <TrendingDown className="inline w-3 h-3 mr-1" />}
                          ${rowNet.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-linen border-t-2 border-charcoal">
                  <tr className="text-charcoal font-medium">
                    <td className="px-3 py-2.5">{year} total</td>
                    {data.channels.map((c) => {
                      const b = data.year_totals.channels[c];
                      const net = (b.revenue || 0) - (b.refunds || 0) - (b.cogs || 0) - (b.fees || 0);
                      return <td key={c} className="px-3 py-2.5 text-right">${net.toFixed(2)}</td>;
                    })}
                    <td className="px-3 py-2.5 text-right">-${data.year_totals.operating_expenses.toFixed(2)}</td>
                    <td className={`px-3 py-2.5 text-right border-l border-charcoal ${data.year_totals.net_profit >= 0 ? "text-emerald-800" : "text-red-800"}`}>
                      ${data.year_totals.net_profit.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function StatCard({ label, value, tone }) {
  const cls = {
    positive: "border-emerald-500/60 bg-emerald-50 text-emerald-800",
    negative: "border-red-500/50 bg-red-50 text-red-800",
  }[tone] || "border-taupe/50 bg-ivory text-charcoal";
  return (
    <div className={`border p-4 ${cls}`}>
      <p className="text-2xl font-serif">{value}</p>
      <p className="text-[0.6rem] tracking-widest uppercase mt-1 text-taupe">{label}</p>
    </div>
  );
}
